import { Link } from '@tanstack/react-router'
import { RingGauge, Sparkline } from '@bloo-team/ui'
import { formatCount } from '@bloo-team/api-client'
import type { HomeWidgetProps } from '@bloo-team/core-shell'
import { useAttackTactics, useCoverageTotals } from '../lib/coverage'
import { usePostureStats } from '../lib/overview'
import { useSignals, useSignalTimeslice } from '../lib/signals'
import { useCmCases, useCmStats } from '../lib/case-mgmt'
import { HOME_WINDOW, ageLabel, deltaView, feedRows, openCaseCount, tacticActivity } from './home-data'

/**
 * Vantage's home widgets (D7): coverage ring, activity feed, open cases,
 * signals · 24h, the three top-N triage tables and the tactic-activity
 * strip. Each widget renders grid items carrying their own `hud-panel` +
 * span classes; the core HomeGrid lays them out by manifest `order`.
 *
 * Live since home-live-v1 — every widget reads a hook the product's own
 * pages already use, so home and the surfaces it links to share query keys
 * (and cache) and can never disagree. Demo mode comes from those hooks
 * (rule 6). The window is fixed at 24h (`HOME_WINDOW`); the join table is
 * docs/home.md.
 */

const p = (w: HomeWidgetProps, to: string) => `/${w.clusterId}/${w.scopeId}${to}` as string
const scopeOf = (w: HomeWidgetProps) => ({ clusterId: w.clusterId, scopeId: w.scopeId })

/** One status line for a widget's pending / failed read — honest, never a zero. */
function ReadState({ pending, error, what }: { pending: boolean; error: boolean; what: string }) {
  if (pending) return <span className="hud-unit" role="status">loading…</span>
  if (error) return <span className="hud-unit" role="status">{what} unavailable</span>
  return null
}

/** Detection coverage ring — `landingpage/coverage` technique totals. */
export function CoverageRing(props: HomeWidgetProps) {
  const { data, isPending, isError } = useCoverageTotals(scopeOf(props))
  const ratio = data && data.total_techniques > 0 ? data.covered_techniques / data.total_techniques : 0
  return (
    <Link to={p(props, '/vantage/coverage')} className="hud-panel hud-span-2" aria-label="Detection coverage">
      <div className="hud-stack">
        <span className="hud-eyebrow"><b>V</b> · coverage</span>
        <ReadState pending={isPending} error={isError} what="coverage" />
        {data ? (
          <RingGauge value={ratio} size={76} color="var(--bui-accent-2)">
            <span className="hud-value" style={{ fontSize: '0.95rem' }}>{data.covered_techniques}</span>
            <span className="hud-unit">/{data.total_techniques}</span>
          </RingGauge>
        ) : null}
      </div>
    </Link>
  )
}

/**
 * Activity feed: signals and newly opened cases over the last 24h, newest
 * first. The right column, three rows tall; span-based (not pinned to a
 * column line) so the grid reflows when other products' widgets are absent.
 */
export function ActivityFeed(props: HomeWidgetProps) {
  const scope = scopeOf(props)
  const signalsQ = useSignals(scope, HOME_WINDOW)
  const casesQ = useCmCases(scope, { page: 1, pageSize: 50 })
  // "Now" is when the reads landed — pure in render, and refreshed with them
  // (refetch on focus / stale), so ages never drift from the rows they label.
  const now = Math.max(signalsQ.dataUpdatedAt, casesQ.dataUpdatedAt)
  const rows = feedRows(signalsQ.signals, casesQ.data?.items ?? [], now)
  return (
    <div className="hud-panel hud-span-4" style={{ gridRow: 'span 3' }} aria-label="Activity · 24h">
      <div className="hud-stack">
        <div className="hud-row hud-row-between">
          <span className="hud-eyebrow">activity<span className="app-live-dot" /></span>
          <span className="hud-unit">last 24h</span>
        </div>
        <ReadState pending={signalsQ.isPending} error={signalsQ.isError} what="signals" />
        {/* A failed cases read is said, not folded into "no new cases" — the feed still shows the signals. */}
        {casesQ.isError ? <span className="hud-unit" role="status">cases unavailable</span> : null}
        {!signalsQ.isPending && !signalsQ.isError && !casesQ.isPending && !casesQ.isError && rows.length === 0 ? (
          <span className="hud-unit" role="status">no signals or new cases in the last 24h</span>
        ) : null}
        {rows.length ? (
          <div className="feed">
            {rows.map((r) => (
              <Link
                key={r.key}
                to={p(props, r.kind === 'Case' ? `/vantage/cases/${r.key.slice(2)}` : '/vantage/signals')}
                className="feed-item"
                data-severity={r.severity}
              >
                <span className="feed-time">{ageLabel(r.at, now)}</span>
                <span className="feed-text">{r.text}</span>
                <span className="feed-tag">{r.kind}</span>
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Cases opened in the last 24h and still open (every V2 status category but
 * closed), from the one case-metrics read; the drift arrow is cases/stats'.
 */
export function CasesCard(props: HomeWidgetProps) {
  const scope = scopeOf(props)
  const statsQ = useCmStats(scope, HOME_WINDOW)
  const postureQ = usePostureStats(scope, HOME_WINDOW)
  const open = openCaseCount(statsQ.data?.counts)
  const delta = deltaView(postureQ.data?.cases.percentDiff)
  return (
    <Link
      to={p(props, '/vantage/cases')}
      className="hud-panel hud-span-2"
      data-severity={open ? 'high' : undefined}
      aria-label="Open cases"
    >
      <div className="hud-stack">
        <span className="hud-eyebrow"><b>V</b> · cases</span>
        <ReadState pending={statsQ.isPending} error={statsQ.isError} what="cases" />
        {open !== null ? (
          <>
            <div className="hud-row" style={{ alignItems: 'baseline', gap: 'var(--bloo-space-2)' }}>
              <span className="hud-value">{formatCount(open)}</span>
              {delta ? <span className="hud-delta" data-dir={delta.dir} data-good="false">{delta.label}</span> : null}
            </div>
            <span className="hud-unit">
              still open · {formatCount(statsQ.data!.created)} opened 24h
            </span>
          </>
        ) : null}
      </div>
    </Link>
  )
}

/** Signals over the last 24h — `cases/stats` count + drift, hourly `timeslice_data` trend. */
export function SignalsCard(props: HomeWidgetProps) {
  const scope = scopeOf(props)
  const postureQ = usePostureStats(scope, HOME_WINDOW)
  const trendQ = useSignalTimeslice(scope, HOME_WINDOW)
  const delta = deltaView(postureQ.data?.signals.percentDiff)
  const trend = (trendQ.data ?? []).map((b) => b.count)
  return (
    <Link to={p(props, '/vantage/signals')} className="hud-panel hud-span-3" data-severity="medium" aria-label="Signals · 24h">
      <div className="hud-stack">
        <span className="hud-eyebrow"><b>V</b> · signals · 24h<span className="app-live-dot" /></span>
        <ReadState pending={postureQ.isPending} error={postureQ.isError} what="signals" />
        {postureQ.data ? (
          <div className="hud-row" style={{ alignItems: 'baseline', gap: 'var(--bloo-space-2)' }}>
            <span className="hud-value">{formatCount(postureQ.data.signals.count)}</span>
            {delta ? <span className="hud-delta" data-dir={delta.dir} data-good="false">{delta.label}</span> : null}
          </div>
        ) : null}
        {trend.length >= 2 ? <Sparkline data={trend} width={200} height={24} /> : null}
      </div>
    </Link>
  )
}

/** The three top-N triage tables — three grid items from one widget, one shared read. */
export function TriageTables(props: HomeWidgetProps) {
  const { data, isPending, isError } = usePostureStats(scopeOf(props), HOME_WINDOW)
  const tables = [
    ['top detections', '/vantage/detections', data?.topDetections],
    ['top suspects', '/vantage/signals', data?.topSuspects],
    ['top targets', '/vantage/signals', data?.topTargets],
  ] as const
  return (
    <>
      {tables.map(([label, to, all]) => {
        const rows = (all ?? []).slice(0, 5)
        const max = rows[0]?.count ?? 0
        return (
          <Link key={label} to={p(props, to)} className="hud-panel hud-span-3" aria-label={`${label} · 24h`}>
            <div className="hud-stack">
              <span className="hud-eyebrow">{label} · 24h</span>
              <ReadState pending={isPending} error={isError} what={label} />
              {data && rows.length === 0 ? <span className="hud-unit" role="status">none in the last 24h</span> : null}
              {rows.length ? (
                <table className="topn">
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.name}>
                        <td className="lbl">
                          {r.name}
                          <span className="bar" style={{ width: max ? `${(r.count / max) * 100}%` : 0 }} />
                        </td>
                        <td className="n">{formatCount(r.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </div>
          </Link>
        )
      })}
    </>
  )
}

/**
 * Tactic activity · 24h: one cell per ATT&CK tactic, shaded by the window's
 * signal count, amber when any fired. Replaces the 14×4 coverage mini-matrix,
 * whose per-bucket coverage needs `attack/analytics` (~2.4 MB) — too heavy
 * for a landing page (docs/overview.md §Skipped by design).
 */
export function MitreMini(props: HomeWidgetProps) {
  const scope = scopeOf(props)
  const tacticsQ = useAttackTactics(scope)
  const postureQ = usePostureStats(scope, HOME_WINDOW)
  const cells = tacticsQ.data && postureQ.data ? tacticActivity(tacticsQ.data, postureQ.data.topTactics) : []
  return (
    <Link to={p(props, '/vantage/coverage')} className="hud-panel hud-span-3" aria-label="Tactic activity · 24h">
      <div className="hud-stack">
        <div className="hud-row hud-row-between">
          <span className="hud-eyebrow"><b>V</b> · tactics · 24h</span>
          <span className="hud-unit">amber = signals</span>
        </div>
        <ReadState
          pending={tacticsQ.isPending || postureQ.isPending}
          error={tacticsQ.isError || postureQ.isError}
          what="tactic activity"
        />
        {cells.length ? (
          <div className="mitre" style={{ ['--cols' as string]: cells.length }}>
            {cells.map((c) => (
              <span
                key={c.name}
                className="mitre-cell"
                title={`${c.name} · ${formatCount(c.count)}`}
                style={{ ['--v' as string]: Math.max(c.v, 6) }}
                data-hot={c.count > 0 || undefined}
              />
            ))}
          </div>
        ) : null}
      </div>
    </Link>
  )
}
